import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FaUpload, FaTimes, FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AssignmentService from '../../../../services/assignmentService';

const CreateAssignmentModal = ({ show, onHide, batchId, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [totalMarks, setTotalMarks] = useState(100);
  const [status, setStatus] = useState('draft');
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setTotalMarks(100);
    setStatus('draft');
    setAttachment(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are allowed');
        return;
      }
      setAttachment(file);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required');
      return;
    }

    const formData = new FormData();
    formData.append('batch_id', batchId);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('due_date', dueDate);
    formData.append('total_marks', totalMarks);
    formData.append('status', status);
    if (attachment) {
      formData.append('attachment', attachment);
    }

    try {
      setLoading(true);
      await AssignmentService.createAssignment(formData);
      toast.success('Assignment created successfully');
      resetForm();
      onSuccess();
      onHide();
    } catch (error) {
      console.error('Failed to create assignment:', error);
      toast.error(error.response?.data?.message || 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="me-2 text-primary" />
          Create New Assignment
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter assignment title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description / Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder="Describe the assignment requirements, guidelines, etc."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Due Date & Time <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <Form.Text className="text-muted">
              Students can submit until this date and time
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Total Marks</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="1000"
              value={totalMarks}
              onChange={(e) => setTotalMarks(parseInt(e.target.value) || 0)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Draft (Not visible to students)"
                name="status"
                value="draft"
                checked={status === 'draft'}
                onChange={(e) => setStatus(e.target.value)}
              />
              <Form.Check
                inline
                type="radio"
                label="Published (Visible to students)"
                name="status"
                value="published"
                checked={status === 'published'}
                onChange={(e) => setStatus(e.target.value)}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Attachment (Optional)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
            />
            {attachment && (
              <div className="mt-2">
                <Alert variant="info" className="py-1">
                  <FaFilePdf className="me-1" /> {attachment.name}
                </Alert>
              </div>
            )}
            <Form.Text className="text-muted">
              Upload assignment PDF or question paper (Max 10MB)
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          <FaTimes className="me-2" /> Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Creating...
            </>
          ) : (
            <>
              <FaUpload className="me-2" /> Create Assignment
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateAssignmentModal;